import { useEffect, useRef } from "react";
import { CALL_STATES } from "../internalDialerContext";

// Plays a soft single-tone "chime" every 2 seconds while callState ===
// INCOMING. The ringtone is synthesized with the Web Audio API so there's
// no audio asset to ship. Browsers gate audio playback behind a prior user
// gesture; collectors have already logged in (a gesture) before this fires,
// so start() succeeds in practice, but we swallow NotAllowedError just in
// case (the modal still appears).
function playChime(audioCtx) {
  const now = audioCtx.currentTime;
  const duration = 0.6;

  const osc = audioCtx.createOscillator();
  osc.type = "sine";
  osc.frequency.setValueAtTime(523.25, now); // C5 -- gentler than a phone bell

  const gain = audioCtx.createGain();
  // Envelope: quick attack, slow decay -- avoids the harsh "pulse" feel.
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(0.12, now + 0.05);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);

  osc.connect(gain);
  gain.connect(audioCtx.destination);

  osc.start(now);
  osc.stop(now + duration + 0.05);
}

export default function useIncomingCallRingtone(callState) {
  const audioCtxRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (callState !== CALL_STATES.INCOMING) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (audioCtxRef.current) {
        audioCtxRef.current.close().catch(() => {});
        audioCtxRef.current = null;
      }
      return undefined;
    }

    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return undefined;
      const audioCtx = new AudioCtx();
      audioCtxRef.current = audioCtx;

      const tick = () => {
        if (audioCtxRef.current !== audioCtx) return;
        playChime(audioCtx);
      };
      tick();
      intervalRef.current = setInterval(tick, 2000);
    } catch {
      // Audio init failed (autoplay policy, no device). Swallow; the
      // visual modal still indicates the incoming call.
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (audioCtxRef.current) {
        audioCtxRef.current.close().catch(() => {});
        audioCtxRef.current = null;
      }
    };
  }, [callState]);
}
